import { useEffect, useState } from 'react'
import { useWorkspaceStore, type Workspace } from '../../stores/workspaceStore'
import { emailDomainApi, type EmailDomainConfig } from '../../services/api'
import ConfigPanel from './ConfigPanel'
import EmailDomainSetup from '../EmailDomainSetup'
import SmsConfigSection from './SmsConfigSection'
import { Badge, Button, Card, CardHeader, Inline, Section, Stack } from '../../ds'

/**
 * Channels — how a message physically leaves, and nothing else.
 *
 * Relocated out of `screens/Account.tsx`'s Integrations tab (App IA v5 · phase 2c, PR 2).
 * `settings-consolidation.md` §3.6: *"connection state and credentials only — no
 * per-campaign enablement."*
 *
 * ⚠️ **The second half of that is not true yet, and this tab must not pretend otherwise.**
 * The SMS drawer below renders `SmsConfigSection`, which still writes
 * `smsConfig.enabledCampaignTypes` — per-campaign enablement, on this surface. It is
 * pre-existing, and MAR-112 (which deleted the `browseFollowUp`/`campaigns.*` gate and the
 * Touchpoints tab) did not take it along; naming it here is cheaper than a docblock that
 * reads as a completed claim.
 *
 * ⚠️ **Shopify, its webhooks and the tracking script are NOT here.** They went to
 * **Store**. §3.6 puts the Shopify connection there, and the v5 prototype's Channels tab
 * renders email and text messages only. Shopify is where the shop's facts come FROM; it
 * is not a channel Marva speaks through, and the tracking script is an inbound stream,
 * not an outbound one.
 *
 * ⚠️ **The v5 design's intro line does not ship.** It reads *"She picks the channel per
 * decision and works down to the first one inside its limits."* The first half is true —
 * `channel` is a model output on every decision (`agent-decision.js:937`, `:2431`), not a
 * per-trigger constant. The second half is **false**: there is one governance call site on
 * the send path (`executor-router.js:1617`) and a block `return`s (`:1679`), which
 * `decision-logger.js:1603` writes as a `suppressed` row. Nothing re-enters
 * `executeDecision` on another channel. Quiet hours defer the *same* channel and chat
 * queues for reconnect; neither is a ladder.
 *
 * `engagement.channelPriority` — stored, schema'd and read by nothing, the same defect one
 * tab over — was deleted outright (MAR-159).
 *
 * ⚠️ The caps, the per-customer limit and the quiet hours are **Limits'**, and this
 * surface must not write them. `SmsConfigSection` already carries only the Channels half
 * of `smsConfig` and writes it through `patchSmsConfig` — see that module's docblock for
 * why a whole-object PUT makes two half-writers silently revert each other.
 */

type ChannelDrawer = 'email' | 'sms'

/**
 * What we know about the email domain, and for WHICH workspace.
 *
 * ⚠️ Three states, not two. `EmailDomainConfig | null` conflated "not read yet" with
 * "read, and there is none", and `.catch(() => setEmailConfig(null))` folded a FAILED read
 * into the same value — so a transient error made a verified domain read "Set up", which
 * is an invitation to redo work that is already done. And with no workspace stamp the
 * previous workspace's domain stayed on screen through a switch until the new read landed.
 *
 * This is the shape PR #103 was reviewed over three times (memory:
 * `feedback_scope_cached_state_at_read`): name every dimension in the stored value, and
 * scope it at the read rather than patching it in an effect.
 */
type EmailState =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; config: EmailDomainConfig | null }

function ChannelList({ workspace }: { workspace: Workspace | null }) {
  const [emailCache, setEmailCache] = useState<{ workspaceId: string; state: EmailState } | null>(null)
  const [drawer, setDrawer] = useState<ChannelDrawer | null>(null)
  const workspaceId = workspace?.id

  // Re-read on close, so a drawer that just verified a domain updates the row behind it.
  useEffect(() => {
    if (!workspaceId) return
    let active = true
    setEmailCache({ workspaceId, state: { status: 'loading' } })
    emailDomainApi
      .get(workspaceId)
      .then((r) => {
        if (!active) return
        // ⚠️ 404 is not a failure. The service answers 404 for a workspace that has no
        // sending domain yet (`domain-verification-api/src/index.js:289`), which is the
        // ordinary starting state and must read as "Set up". Anything else that failed is
        // genuinely unknown and must NOT — telling a merchant with a verified domain to set
        // one up is an invitation to redo work that is already done.
        if (r.success) {
          setEmailCache({ workspaceId, state: { status: 'ready', config: r.data ?? null } })
        } else if (r.status === 404) {
          setEmailCache({ workspaceId, state: { status: 'ready', config: null } })
        } else {
          setEmailCache({ workspaceId, state: { status: 'error' } })
        }
      })
      .catch(() => { if (active) setEmailCache({ workspaceId, state: { status: 'error' } }) })
    // ⚠️ Cancel on unmount AND on a workspace switch, or a slow read for the workspace the
    // merchant just left resolves last and overwrites the one they are looking at.
    return () => { active = false }
  }, [workspaceId, drawer])

  const twilio = workspace?.twilio
  const smsEnabled = !!workspace?.smsConfig?.enabled

  // ── Scoped at the READ. A cache from another workspace is simply not current. ──
  const email: EmailState =
    emailCache && emailCache.workspaceId === workspaceId ? emailCache.state : { status: 'loading' }
  const emailConfig = email.status === 'ready' ? email.config : null
  const emailVerified = emailConfig?.verificationStatus === 'verified'

  return (
    <Section
      title="How she reaches people"
      description="Email and text messages, set up here. She picks a channel for each decision as she makes it. How far she may go on one, and the hours she stays quiet, are set under Limits — and a message that would cross one of those is withheld, not moved to another channel."
    >
      <Stack gap="sm">
        <Card>
          <CardHeader
            title={`Email${emailConfig?.fromAddress ? ` — ${emailConfig.fromAddress}` : ''}`}
            description={
              <>
                {email.status === 'loading' && 'Checking your sending domain…'}
                {email.status === 'error' && "We couldn't check your sending domain just now. Nothing has changed."}
                {email.status === 'ready' && (emailVerified
                  ? 'Domain verified · SPF, DKIM and DMARC passing.'
                  : 'Send from your own domain with verified deliverability.')}
              </>
            }
            // Inline, not a fragment: the card's aside slot has no gap, so the badge touched the button.
            aside={
              <Inline wrap={false}>
                {emailVerified && <Badge tone="sage" dot>Verified</Badge>}
                {/* ⚠️ Never "Set up" on a failed or pending read — that invites a merchant to redo
                    a domain they have already verified. Only a READ that succeeded may say so. */}
                <Button variant="ghost" size="sm" disabled={email.status !== 'ready'} onClick={() => setDrawer('email')}>
                  {email.status !== 'ready' ? 'Manage' : emailVerified ? 'Manage' : emailConfig ? 'Continue setup' : 'Set up'}
                </Button>
              </Inline>
            }
          />
        </Card>

        <Card>
          <CardHeader
            title="Text messages — your Twilio"
            description={
              !twilio?.configured
                // Not connected yet: say what connecting does, not what it would be once done.
                ? 'Connect your own Twilio account — billed to you, and you keep the number.'
                : !smsEnabled
                  ? 'Your Twilio account is connected, but SMS is switched off — turn it on in Manage.'
                  : 'Connected through your own Twilio account, billed to you. You keep the number.'
            }
            aside={
              <Inline wrap={false}>
                {/* ⚠️ Two different facts. `twilio.configured` means credentials are stored;
                    `smsConfig.enabled` is what actually lets a message out. A single "Connected"
                    pill reading only the first told a merchant SMS was live while it was off. */}
                {twilio?.configured && (
                  smsEnabled
                    ? <Badge tone="sage" dot>On</Badge>
                    : <Badge dot>Connected, off</Badge>
                )}
                <Button variant="ghost" size="sm" onClick={() => setDrawer('sms')}>
                  {twilio?.configured ? 'Manage' : 'Connect'}
                </Button>
              </Inline>
            }
          />
        </Card>

        <Card tone="recessed">
          <CardHeader
            title="WhatsApp"
            description="Reach customers who prefer WhatsApp."
            aside={<Badge dot>Soon</Badge>}
          />
        </Card>
      </Stack>

      <ConfigPanel title="Email domain" isOpen={drawer === 'email'} onClose={() => setDrawer(null)}>
        <EmailDomainSetup />
      </ConfigPanel>
      <ConfigPanel title="Text messages — Twilio" isOpen={drawer === 'sms'} onClose={() => setDrawer(null)}>
        <SmsConfigSection onClose={() => setDrawer(null)} />
      </ConfigPanel>
    </Section>
  )
}

/** What the Channels tab renders. Reads the workspace itself, as Store does. */
export default function ChannelsSection() {
  const { currentWorkspace } = useWorkspaceStore()
  return <ChannelList workspace={currentWorkspace} />
}
